import React, { useMemo, useState } from "react";
import { HourlyActivity } from "../../../components/funnel/HourlyActivity";
import { useGridCrosshair } from "../../../components/funnel/useGridCrosshair";
import { FunnelView, ViewSwitch } from "../../../components/funnel/ViewSwitch";
import { WaveChart } from "../../../components/funnel/WaveChart";
import { getHeatClass } from "../../../components/funnel/utils";
import { FunnelSection, useFunnelData } from "../../../hooks/useFunnelData";
import { PreEligibilityHourlyData } from "../../../services/funnelService";
import { SalesDropApiResponse } from "./API";
import "../../../components/funnel/funnel-minimal.css";

const STAGE_COUNT_MAP: Array<{
  label: string;
  key: keyof SalesDropApiResponse;
}> = [
  { label: "Preloan Eligibility", key: "preloanEligibilityCount" },
  { label: "Created", key: "createdCount" },
  { label: "Verified", key: "verifiedUniqueCount" },
  { label: "App Custcared", key: "appCustcaredCount" },
  { label: "Call Initialized", key: "callInitializedUniqueCount" },
  { label: "Call Approved", key: "callApprovedUniqueCount" },
  { label: "Contract Signed", key: "contractSignedUniqueCount" },
  { label: "Sanad Created", key: "sanadCreatedUniqueCount" },
  { label: "Nafaes Sales Ordered", key: "nafaesSalesOrderedCount" },
  { label: "Disbursed", key: "disbursedCount" },
  { label: "Withdrawal Requested", key: "withdrawalRequestedCount" },
  { label: "Withdrawal Success", key: "withdrawalSuccessCount" },
];

const DROP_METRICS: Array<{
  label: string;
  percentKey: keyof SalesDropApiResponse;
  alertKey: keyof SalesDropApiResponse;
  redBelow?: number;
}> = [
  {
    label: "Preloan → Created",
    percentKey: "preloanToCreatedDropPercent",
    alertKey: "preloanToCreatedAlert",
  },
  {
    label: "Created → Verified",
    percentKey: "createdToVerifiedDropPercent",
    alertKey: "createdToVerifiedAlert",
  },
  {
    label: "Verified → App Custcared",
    percentKey: "verifiedToAppCustcaredDropPercent",
    alertKey: "verifiedToAppCustcaredAlert",
  },
  {
    label: "App Custcared → Call Initialized",
    percentKey: "appCustcaredToCallInitializedDropPercent",
    alertKey: "appCustcaredToCallInitializedAlert",
  },
  {
    label: "Call Initialized → Call Approved",
    percentKey: "callInitializedToCallApprovedDropPercent",
    alertKey: "callInitializedToCallApprovedAlert",
  },
  {
    label: "Call Approved → Contract Signed",
    percentKey: "callApprovedToContractSignedDropPercent",
    alertKey: "callApprovedToContractSignedAlert",
  },
  {
    label: "Contract Signed → Sanad Created",
    percentKey: "contractSignedToSanadCreatedDropPercent",
    alertKey: "contractSignedToSanadCreatedAlert",
    redBelow: 80,
  },
  {
    label: "Sanad Created → Nafaes Sales Ordered",
    percentKey: "sanadCreatedToNafaesSalesOrderedDropPercent",
    alertKey: "sanadCreatedToNafaesSalesOrderedAlert",
  },
];

const THRESHOLD_VALUES: Array<{ label: string; range: string }> = [
  { label: "Pre eligibility Drop", range: "50% - 52%" },
  { label: "Loan Request", range: "85% - 90%" },
  { label: "Approval rate", range: "18% - 22%" },
  { label: "Accept offer", range: "45% - 50%" },
  { label: "Call Approved", range: "75% - 85%" },
  { label: "Contract signed", range: "80% - 90%" },
  { label: "E-Promissory Signed", range: "88% - 95%" },
];

// Pre-eligibility count columns, each shaded against its own maximum. The eligible /
// not-eligible percentages are shown together as one split bar after these.
const PRE_ELIGIBILITY_COLUMNS: Array<{
  key: Exclude<
    keyof PreEligibilityHourlyData,
    "hour_range" | "eligible_percent" | "not_eligible_percent"
  >;
  label: React.ReactNode;
  // Plain-text name for the Wave view's hover list.
  name: string;
}> = [
  {
    key: "total_pre_eligibility",
    name: "Total Pre-Eligibility",
    label: (
      <>
        Total
        <br />
        Pre-Eligibility
      </>
    ),
  },
  {
    key: "pre_registration",
    label: "Pre Registration",
    name: "Pre Registration",
  },
  {
    key: "post_registration",
    label: "Post Registration",
    name: "Post Registration",
  },
  {
    key: "middleware_customer_block",
    name: "Middleware Customer Block",
    label: (
      <>
        Middleware
        <br />
        Customer Block
      </>
    ),
  },
  { key: "eligible_count", label: "Eligible", name: "Eligible" },
  { key: "not_eligible_count", label: "Not-Eligible", name: "Not-Eligible" },
];

const formatHourTitle = (label: string): string => `Hour ${label}`;

// Wave | Bar switch for the 24-hour sections. Both show bars only for now (user request);
// set a flag to true to bring back that section's switch (Wave code is kept).
const STAGE_COUNTS_WAVE_ENABLED = false;
const SALES_SUCCESS_WAVE_ENABLED = false;

// Long metric names ("A → B") wrap onto two lines under the Wave view's points.
const splitMetricLabel = (label: string): string[] => {
  const [from, to] = label.split(" → ");
  return to ? [`${from} →`, to] : [label];
};

// Below this % a Sales Success bar is too short for its value, so the value sits after it.
const SUCCESS_LABEL_INSIDE_MIN = 18;

// Plot height range of the stage bar chart, and the smallest visible height for a non-zero bar.
const STAGE_BAR_PLOT_MIN_HEIGHT = 180;
const STAGE_BAR_PLOT_MAX_HEIGHT = 360;
// Keeps gridlines and axis numbers from crowding when there are many axis steps.
const STAGE_BAR_PX_PER_TICK = 32;
const STAGE_BAR_MIN_HEIGHT = 4;
// Bars shorter than this show their count above the bar instead of inside it.
const STAGE_BAR_INSIDE_LABEL_MIN = 22;

// Axis step follows the size of the largest count: up to 10 → 1, up to 100 → 10,
// up to 1000 → 100, and so on. The axis ends at the next step above the largest count.
const getAxisTicks = (maxValue: number): number[] => {
  const safeMax = Math.max(1, maxValue);
  const step = Math.max(1, 10 ** Math.ceil(Math.log10(safeMax)) / 10);
  const top = Math.max(step, Math.ceil(safeMax / step) * step);
  const ticks: number[] = [];
  for (let tick = 0; tick <= top; tick += step) {
    ticks.push(tick);
  }
  return ticks;
};

const SECTION_ORDER: FunnelSection[] = [
  "hourly",
  "preEligibility",
  "salesDrop",
];

const SectionLoader: React.FC = () => (
  <div
    className="funnel-minimal-section-loader"
    role="status"
    aria-label="Loading"
  >
    <span className="funnel-minimal-spinner" aria-hidden="true"></span>
  </div>
);

// One bar split into eligible (violet) and not-eligible (grey) shares, with each % inside its part.
const EligibilitySplit: React.FC<{
  eligiblePercent: number;
  notEligiblePercent: number;
}> = ({ eligiblePercent, notEligiblePercent }) => {
  const total = eligiblePercent + notEligiblePercent;
  return (
    <div
      className="funnel-minimal-split-bar"
      title={`Eligible ${eligiblePercent.toFixed(2)}% · Not-Eligible ${notEligiblePercent.toFixed(2)}%`}
    >
      {/* Each part grows with its share; a minimum width (CSS) keeps its value readable. */}
      {eligiblePercent > 0 && (
        <span
          className="funnel-minimal-split-eligible"
          style={{ flexGrow: eligiblePercent / total }}
        >
          {eligiblePercent.toFixed(2)}%
        </span>
      )}
      {notEligiblePercent > 0 && (
        <span
          className="funnel-minimal-split-not-eligible"
          style={{ flexGrow: notEligiblePercent / total }}
        >
          {notEligiblePercent.toFixed(2)}%
        </span>
      )}
    </div>
  );
};

export const FunnelMonitoring: React.FC = () => {
  const preEligibilityCrosshair = useGridCrosshair();
  const {
    hourlyData,
    preEligibilityHourlyData,
    salesDropData,
    lastUpdated,
    loading,
    loadingSection,
    reload,
  } = useFunnelData();

  const [preEligibilityView, setPreEligibilityView] =
    useState<FunnelView>("wave");
  const [stageCountsView, setStageCountsView] = useState<FunnelView>(
    STAGE_COUNTS_WAVE_ENABLED ? "wave" : "bar",
  );
  const [salesSuccessView, setSalesSuccessView] = useState<FunnelView>(
    SALES_SUCCESS_WAVE_ENABLED ? "wave" : "bar",
  );

  // Wave views read left to right in time; hourly rows arrive latest hour first.
  const preEligibilityWaveRows = useMemo(
    () => [...preEligibilityHourlyData].reverse(),
    [preEligibilityHourlyData],
  );
  const preEligibilityWave = useMemo(
    () => ({
      labels: preEligibilityWaveRows.map((row) => row.hour_range),
      series: PRE_ELIGIBILITY_COLUMNS.map((column) => ({
        label: column.name,
        data: preEligibilityWaveRows.map((row) => Number(row[column.key] ?? 0)),
      })),
    }),
    [preEligibilityWaveRows],
  );
  // The eligible / not-eligible split is a percentage, so it goes in the hover list
  // instead of sharing the counts' axis.
  const preEligibilityFooter = useMemo(
    () => (index: number) => {
      const row = preEligibilityWaveRows[index];
      if (!row) {
        return undefined;
      }
      return `Eligible ${Number(row.eligible_percent ?? 0).toFixed(2)}% · Not-Eligible ${Number(row.not_eligible_percent ?? 0).toFixed(2)}%`;
    },
    [preEligibilityWaveRows],
  );

  const isSectionFetched = (section: FunnelSection): boolean =>
    loadingSection === null ||
    SECTION_ORDER.indexOf(loadingSection) > SECTION_ORDER.indexOf(section);

  const showHourly = hourlyData.length > 0 || isSectionFetched("hourly");

  // Stage counts are a single 24-hour snapshot (no hours), shown as one bar per stage.
  const stageCountTicks = getAxisTicks(
    Math.max(
      0,
      ...STAGE_COUNT_MAP.map((stage) =>
        Number(salesDropData?.[stage.key] ?? 0),
      ),
    ),
  );
  const stageAxisMax = stageCountTicks[stageCountTicks.length - 1];
  const stagePlotHeight = Math.min(
    STAGE_BAR_PLOT_MAX_HEIGHT,
    Math.max(
      STAGE_BAR_PLOT_MIN_HEIGHT,
      (stageCountTicks.length - 1) * STAGE_BAR_PX_PER_TICK,
    ),
  );

  const preEligibilityMax = PRE_ELIGIBILITY_COLUMNS.map((column) =>
    Math.max(
      0,
      ...preEligibilityHourlyData.map((row) => Number(row[column.key] ?? 0)),
    ),
  );
  const getClampedDropPercent = (value: number): number => {
    return Math.max(0, value);
  };

  const getSalesMetricClass = (
    alertValue: number,
    percent: number,
    redBelow?: number,
  ): string => {
    const isCritical =
      redBelow !== undefined
        ? alertValue === 1 && percent < redBelow
        : alertValue === 1;
    return isCritical ? "funnel-minimal-metric-critical" : "";
  };

  // Stage Counts / Sales Success are single 24-hour values per stage, so their wave runs
  // across the stages instead of across hours.
  const stageCountsWave = {
    labels: STAGE_COUNT_MAP.map((stage) => stage.label.split(" ")),
    series: [
      {
        label: "Count",
        data: STAGE_COUNT_MAP.map((stage) =>
          Number(salesDropData?.[stage.key] ?? 0),
        ),
        color: "#2a78d6",
      },
    ],
  };
  const salesSuccessRows = DROP_METRICS.map((metric) => {
    const percent = getClampedDropPercent(
      Number(salesDropData?.[metric.percentKey] ?? 0),
    );
    const alertValue = Number(salesDropData?.[metric.alertKey] ?? 0);
    return {
      percent,
      // Same rule as the bar view; only the label colour uses it.
      isCritical:
        getSalesMetricClass(alertValue, percent, metric.redBelow) ===
        "funnel-minimal-metric-critical",
    };
  });
  const salesSuccessWave = {
    labels: DROP_METRICS.map((metric) => splitMetricLabel(metric.label)),
    series: [
      {
        label: "Success",
        data: salesSuccessRows.map((row) => row.percent),
        color: "#0f9488",
        valueColors: salesSuccessRows.map((row) =>
          row.isCritical ? "#b42323" : "#0f172a",
        ),
      },
    ],
  };
  const salesSuccessFooter = (index: number) =>
    salesSuccessRows[index]?.isCritical ? "Critical" : undefined;

  return (
    <div className="funnel-minimal-page">
      {loadingSection === "hourly" && <SectionLoader />}

      {showHourly && (
        <>
          <div className="funnel-minimal-header">
            <div className="funnel-minimal-header-actions">
              <span className="funnel-minimal-last-updated">
                Last Updated:{" "}
                {lastUpdated
                  ? lastUpdated.toLocaleTimeString()
                  : "Not loaded yet"}
              </span>
              <button
                type="button"
                className="funnel-minimal-refresh-btn"
                onClick={reload}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise"></i>
                Refresh
              </button>
            </div>
          </div>

          <HourlyActivity rows={hourlyData} />
        </>
      )}

      {loadingSection === "preEligibility" && <SectionLoader />}

      {/* Hidden entirely when the API returns no rows. */}
      {preEligibilityHourlyData.length > 0 && (
        <section className="funnel-minimal-section">
          <div className="funnel-minimal-heading-row">
            <div className="funnel-minimal-heading">
              <h2 className="funnel-minimal-heading-title">
                Pre-Eligibility (Hourly)
              </h2>
              <p className="funnel-minimal-heading-subtitle">
                Pre-eligibility checks by hour
              </p>
            </div>
            <ViewSwitch
              value={preEligibilityView}
              onChange={setPreEligibilityView}
            />
          </div>

          {preEligibilityView === "wave" ? (
            <WaveChart
              labels={preEligibilityWave.labels}
              series={preEligibilityWave.series}
              tooltipTitle={formatHourTitle}
              tooltipFooter={preEligibilityFooter}
              showLegend
              hoverMode="point"
            />
          ) : (
            <div className="funnel-minimal-hourly-grid-wrap">
              <table
                className="funnel-minimal-hourly-grid funnel-minimal-hourly-grid-violet funnel-minimal-hourly-grid-compact"
                {...preEligibilityCrosshair}
              >
                <thead>
                  <tr>
                    <th className="funnel-minimal-hourly-grid-hour">Hour</th>
                    {PRE_ELIGIBILITY_COLUMNS.map((column) => (
                      <th key={`pre-eligibility-header-${column.key}`}>
                        {column.label}
                      </th>
                    ))}
                    <th className="funnel-minimal-split-header">
                      Eligible vs Not-Eligible
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {preEligibilityHourlyData.map((row) => (
                    <tr key={`pre-eligibility-${row.hour_range}`}>
                      <th
                        scope="row"
                        className="funnel-minimal-hourly-grid-hour"
                      >
                        {row.hour_range}
                      </th>
                      {PRE_ELIGIBILITY_COLUMNS.map((column, index) => {
                        const value = Number(row[column.key] ?? 0);
                        return (
                          <td
                            key={`pre-eligibility-${row.hour_range}-${column.key}`}
                            className={getHeatClass(
                              value,
                              preEligibilityMax[index],
                            )}
                          >
                            {value.toLocaleString()}
                          </td>
                        );
                      })}
                      <td className="funnel-minimal-split-cell">
                        <EligibilitySplit
                          eligiblePercent={Number(row.eligible_percent ?? 0)}
                          notEligiblePercent={Number(
                            row.not_eligible_percent ?? 0,
                          )}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {loadingSection === "salesDrop" && <SectionLoader />}

      {salesDropData && (
        <>
          <section className="funnel-minimal-section">
            <div className="funnel-minimal-heading-row">
              <div className="funnel-minimal-heading">
                <h2 className="funnel-minimal-heading-title">
                  Stage Counts (Last 24 hours)
                </h2>
                <p className="funnel-minimal-heading-subtitle">
                  Applications per stage over the last 24 hours
                </p>
              </div>
              {STAGE_COUNTS_WAVE_ENABLED && (
                <ViewSwitch
                  value={stageCountsView}
                  onChange={setStageCountsView}
                />
              )}
            </div>
            {stageCountsView === "wave" ? (
              <WaveChart
                labels={stageCountsWave.labels}
                series={stageCountsWave.series}
                showValues
              />
            ) : (
              <div className="funnel-minimal-stage-bars-wrap">
                <div className="funnel-minimal-stage-chart">
                  <div className="funnel-minimal-stage-axis" aria-hidden="true">
                    {stageCountTicks.map((tick) => (
                      <span
                        key={`stage-axis-${tick}`}
                        style={{ bottom: `${(tick / stageAxisMax) * 100}%` }}
                      >
                        {tick.toLocaleString()}
                      </span>
                    ))}
                  </div>
                  <div
                    className="funnel-minimal-stage-plot"
                    style={{ height: stagePlotHeight }}
                  >
                    {stageCountTicks.slice(1).map((tick) => (
                      <span
                        key={`stage-grid-${tick}`}
                        className="funnel-minimal-stage-gridline"
                        style={{ bottom: `${(tick / stageAxisMax) * 100}%` }}
                      ></span>
                    ))}
                    <div
                      className="funnel-minimal-stage-bars"
                      style={{
                        gridTemplateColumns: `repeat(${STAGE_COUNT_MAP.length}, minmax(0, 1fr))`,
                      }}
                    >
                      {STAGE_COUNT_MAP.map((stage) => {
                        const count = Number(salesDropData[stage.key] ?? 0);
                        const barHeight =
                          count === 0
                            ? 0
                            : Math.max(
                                STAGE_BAR_MIN_HEIGHT,
                                Math.round(
                                  (count / stageAxisMax) * stagePlotHeight,
                                ),
                              );
                        const labelInside =
                          barHeight >= STAGE_BAR_INSIDE_LABEL_MIN;
                        return (
                          <div
                            key={`stage-count-bar-${stage.key}`}
                            className="funnel-minimal-stage-bar-column"
                            title={`${stage.label}: ${count.toLocaleString()}`}
                          >
                            {count > 0 && !labelInside && (
                              <span className="funnel-minimal-stage-bar-value">
                                {count.toLocaleString()}
                              </span>
                            )}
                            <span
                              className={
                                count === 0
                                  ? "funnel-minimal-stage-bar funnel-minimal-stage-bar-zero"
                                  : "funnel-minimal-stage-bar"
                              }
                              style={
                                count === 0 ? undefined : { height: barHeight }
                              }
                            >
                              {labelInside && (
                                <span className="funnel-minimal-stage-bar-inside">
                                  {count.toLocaleString()}
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div></div>
                  <div
                    className="funnel-minimal-stage-labels"
                    style={{
                      gridTemplateColumns: `repeat(${STAGE_COUNT_MAP.length}, minmax(0, 1fr))`,
                    }}
                  >
                    {STAGE_COUNT_MAP.map((stage) => (
                      <span
                        key={`stage-count-label-${stage.key}`}
                        className="funnel-minimal-stage-bar-label"
                      >
                        {stage.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>

          <section className="funnel-minimal-section">
            <div className="funnel-minimal-heading-row">
              <div className="funnel-minimal-heading">
                <h2 className="funnel-minimal-heading-title">
                  Sales Success (%) (Last 24 hours)
                </h2>
                <p className="funnel-minimal-heading-subtitle">
                  Stage-to-stage success over the last 24 hours
                </p>
              </div>
              {SALES_SUCCESS_WAVE_ENABLED && (
                <ViewSwitch
                  value={salesSuccessView}
                  onChange={setSalesSuccessView}
                />
              )}
            </div>
            <div
              className={
                salesSuccessView === "wave"
                  ? "funnel-minimal-sales-layout funnel-minimal-sales-layout-stacked"
                  : "funnel-minimal-sales-layout"
              }
            >
              {salesSuccessView === "wave" ? (
                <div className="funnel-minimal-sales-wave">
                  <WaveChart
                    labels={salesSuccessWave.labels}
                    series={salesSuccessWave.series}
                    isPercent
                    showValues
                    tooltipFooter={salesSuccessFooter}
                  />
                </div>
              ) : (
                <div className="funnel-minimal-table-wrap funnel-minimal-sales-metric-wrap">
                  <div className="funnel-minimal-success-chart">
                    {DROP_METRICS.map((metric) => {
                      const rawPercent = Number(
                        salesDropData[metric.percentKey] ?? 0,
                      );
                      const percent = getClampedDropPercent(rawPercent);
                      const alertValue = Number(
                        salesDropData[metric.alertKey] ?? 0,
                      );
                      // Same rule as before; the class only decides the bar colour now.
                      const isCritical =
                        getSalesMetricClass(
                          alertValue,
                          percent,
                          metric.redBelow,
                        ) === "funnel-minimal-metric-critical";
                      return (
                        <div
                          key={metric.percentKey}
                          className={
                            isCritical
                              ? "funnel-minimal-success-row funnel-minimal-success-row-critical"
                              : "funnel-minimal-success-row"
                          }
                          title={`${metric.label}: ${percent.toFixed(2)}%`}
                        >
                          <span className="funnel-minimal-success-label">
                            {metric.label}
                          </span>
                          <span className="funnel-minimal-success-track">
                            <span
                              className="funnel-minimal-success-fill"
                              style={{ width: `${Math.min(percent, 100)}%` }}
                            >
                              {percent >= SUCCESS_LABEL_INSIDE_MIN && (
                                <span className="funnel-minimal-success-value">
                                  {percent.toFixed(2)}%
                                </span>
                              )}
                            </span>
                            {percent < SUCCESS_LABEL_INSIDE_MIN && (
                              <span
                                className="funnel-minimal-success-value funnel-minimal-success-value-outside"
                                style={{ left: `calc(${percent}% + 6px)` }}
                              >
                                {percent.toFixed(2)}%
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                    <div
                      className="funnel-minimal-success-row funnel-minimal-success-scale"
                      aria-hidden="true"
                    >
                      <span></span>
                      <span className="funnel-minimal-success-scale-ticks">
                        {[0, 25, 50, 75, 100].map((tick) => (
                          <span key={`success-tick-${tick}`}>{tick}%</span>
                        ))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="funnel-minimal-threshold-area">
                <div className="funnel-minimal-table-wrap funnel-minimal-threshold-wrap">
                  <div className="funnel-minimal-threshold-title">
                    Success Thresholds
                  </div>
                  <table className="funnel-minimal-table funnel-minimal-threshold-table">
                    <thead>
                      <tr>
                        {THRESHOLD_VALUES.map((threshold) => (
                          <th key={`threshold-label-${threshold.label}`}>
                            <span className="funnel-minimal-threshold-header-label">
                              {threshold.label}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {THRESHOLD_VALUES.map((threshold) => (
                          <td key={`threshold-range-${threshold.label}`}>
                            {threshold.range}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};
